import React, { useEffect } from 'react';
import { RefreshCw, TrendingUp, Package, DollarSign, PackageMinus, Calendar, Users, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useDashboardStore from '../store/useDashboardStore';
import RecentActivityList from '../components/dashboard/RecentActivityList';
import SalesChart from '../components/dashboard/SalesChart';
import OverdueCustomersWidget from '../components/dashboard/OverdueCustomersWidget';
import StockAlertsWidget from '../components/dashboard/StockAlertsWidget';

/**
 * Home Page Component
 * الصفحة الرئيسية للمندوب - Dashboard
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Clean Page Structure
 * - P-STATE-001: Zustand Integration
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-UX-001: Loading States & Refresh
 * - P034: Data Validation (via useDashboardStore)
 * - P010: Error Handling
 */

const Home = () => {
  const navigate = useNavigate();
  const {
    statistics,
    recentActivity,
    recentActivityLoading,
    salesChartData,
    salesChartLoading,
    error,
    refreshDashboard,
    clearError
  } = useDashboardStore();

  // Load dashboard data on mount
  // refreshDashboard is a stable function from Zustand store
  useEffect(() => {
    refreshDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle refresh button
  const handleRefresh = async () => {
    clearError();
    await refreshDashboard();
  };

  return (
    <div className="min-h-screen p-4 md:p-6 pt-20">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
            الصفحة الرئيسية
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            مرحباً بك في نظام إدارة المندوبين
          </p>
        </div>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">تحديث</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <span className="font-semibold">خطأ:</span>
              <span>{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Today's Performance Summary */}
      <div className="mb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">أداء اليوم</h2>
              <p className="text-blue-100 text-sm">
                {new Date().toLocaleDateString('ar-EG', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/reports/daily')}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-all backdrop-blur-sm"
          >
            عرض التقرير
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* Row 1 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-blue-200" />
              <p className="text-blue-100 text-xs">الفواتير</p>
            </div>
            <p className="text-xl font-bold">{statistics.todaySales.count}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-200" />
              <p className="text-blue-100 text-xs">المبيعات</p>
            </div>
            <p className="text-base font-bold break-all">{parseFloat(statistics.todaySales.total || 0).toFixed(3)} د.ك</p>
          </div>

          {/* Row 2 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-yellow-200" />
              <p className="text-blue-100 text-xs">التحصيلات</p>
            </div>
            <p className="text-base font-bold break-all">{parseFloat(statistics.collections.total || 0).toFixed(3)} د.ك</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-purple-200" />
              <p className="text-blue-100 text-xs">العملاء</p>
            </div>
            <p className="text-xl font-bold">{statistics.todayCustomers.count}</p>
          </div>

          {/* Row 3 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-indigo-200" />
              <p className="text-blue-100 text-xs">المخزون</p>
            </div>
            <p className="text-xl font-bold">{statistics.inventory.totalProducts}</p>
            <p className="text-[10px] text-blue-200 mt-0.5">{statistics.inventory.lowStockCount} منتج ناقص</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <PackageMinus className="w-4 h-4 text-orange-200" />
              <p className="text-blue-100 text-xs">المرتجعات</p>
            </div>
            <p className="text-base font-bold break-all">{parseFloat(statistics.returns.total || 0).toFixed(3)} د.ك</p>
            <p className="text-[10px] text-blue-200 mt-0.5">{statistics.returns.count} مرتجع</p>
          </div>
        </div>
      </div>

      {/* Sales Chart - Full Width */}
      <div className="mb-6">
        <SalesChart
          data={salesChartData}
          loading={salesChartLoading}
        />
      </div>

      {/* Alert Widgets - 2 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Overdue Customers Widget */}
        <OverdueCustomersWidget />

        {/* Stock Alerts Widget */}
        <StockAlertsWidget />
      </div>

      {/* Recent Activity - Full Width */}
      <div className="mb-6">
        <RecentActivityList
          activities={recentActivity}
          loading={recentActivityLoading}
        />
      </div>
    </div>
  );
};

export default Home;
